import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/ui/use-toast';
import BlockJudgesPicker from './BlockJudgesPicker';
import RoundScoreDialog from './RoundScoreDialog';
import { matchesFilter } from '../scoreboard-filter';
import { errorCodeOf } from '../../services/api-error';
import { competitionRoundsService } from '../../services/competition-rounds';
import {
  roundScoresService,
  type AttemptType,
  type RoundScore,
  type RoundScoreClimb,
  type ScoreJudge,
} from '../../services/round-scores';

/** Which entrant's card on which problem is open. */
interface OpenCell {
  score: RoundScore;
  roundClimbId: string;
}

interface RoundBlocksTableProps {
  competitionSlug: string;
  roundId: string;
  scores: RoundScore[];
  climbs: RoundScoreClimb[];
  /** The competition's judges, for handing a judge-only block to some of them. */
  judges: ScoreJudge[];
  /** The open class: a block belongs to the class that climbs it. */
  categoryId: string;
  /** What was typed into the filter box. */
  filter: string;
  /** Whether the viewer may say which blocks need a judge. */
  canSetBlocks: boolean;
  /**
   * Whether the round still takes marks at all. False once it is closed, which
   * ends everybody's turn — the server refuses a card from that moment, so the
   * buttons go with it rather than promising a save that will not happen.
   */
  canScore: boolean;
  /** Whether the board's regulation tells judged blocks apart (OPES) — see RoundScoreDialog. */
  judgedBlocks?: boolean;
  /** One entrant's card as the server saved it. */
  onScoreSaved: (score: RoundScore) => void;
  /**
   * The server having answered that the round is closed. Said out loud because
   * this page is left open all day and the round may have been closed on
   * another one: the page behind can then put its own buttons away instead of
   * waiting to be reloaded.
   */
  onRoundClosed?: () => void;
  /** One block as the server left it — its judge-only flag, its judges. */
  onClimbChanged: (climb: RoundScoreClimb) => void;
}

/** The mark each kind of attempt leaves on a card, as a judge writes it. */
const MARK: Record<AttemptType, string> = { attempt: '|', zone: '+', top: 'T' };

/** Names one cell of the table — an entrant's card on one problem. */
const cellKey = (roundClimbId: string, scoreId: string) => `${roundClimbId}:${scoreId}`;

/**
 * The round read by problem rather than by entrant: one `<tbody>` per block,
 * its name spanning the whole field of people climbing it.
 *
 * The scoreboard beside it answers "how is this athlete doing" — one row per
 * person, their whole card behind a click. This answers the question the judge
 * at the wall actually has: *this* block, everybody on it, in one column they
 * can run a finger down. Same data, same corrections, transposed.
 *
 * **Marks go on from the table itself.** This is the live surface: somebody
 * standing at the wall presses a button as each go happens, and one press is
 * one saved attempt. The dialog is still there for the other job — looking at
 * a card as a whole and taking something off it — and that is what the marks
 * themselves open.
 *
 * Drawn on the round's own page, as the other half of the Athlete/Block switch.
 * **Not a judge's screen**: a judge scores from the member app, and the panel
 * is for the people who run the competition. What the viewer may do is still
 * the `canSetBlocks` prop and the server's `can_score` rather than an
 * assumption about who got this far — those are the only two answers the server
 * will stand behind.
 */
export default function RoundBlocksTable({
  competitionSlug,
  roundId,
  scores,
  climbs,
  judges,
  categoryId,
  filter,
  canSetBlocks,
  canScore,
  judgedBlocks = false,
  onScoreSaved,
  onClimbChanged,
  onRoundClosed,
}: RoundBlocksTableProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [open, setOpen] = useState<OpenCell | null>(null);
  /** The one cell with a mark in flight, so only its own buttons go quiet. */
  const [busyCell, setBusyCell] = useState<string | null>(null);
  /** The one block whose judge-only flag is being saved. */
  const [busyBlock, setBusyBlock] = useState<string | null>(null);

  /**
   * Puts one more mark on one card, there and then.
   *
   * The save is a replacement of that card, so what goes up is the attempts it
   * already had plus this one — which is also why it is safe to press twice in a
   * row: each press sends the whole card as it should then read, rather than an
   * increment that could be applied to a stale count.
   */
  const addMark = async (score: RoundScore, roundClimbId: string, type: AttemptType) => {
    if (!competitionSlug || !roundId || busyCell || !canScore) return;

    const card = score.cards.find((candidate) => candidate.round_climb_id === roundClimbId);
    if (!card) return;

    const attempts = [...card.attempts.map((attempt) => attempt.attempt_type), type];

    setBusyCell(cellKey(roundClimbId, score.id));
    try {
      const saved = await roundScoresService.save(competitionSlug, roundId, score.id, [
        { round_climb_id: roundClimbId, attempts },
      ]);
      onScoreSaved(saved);
    } catch (error) {
      toast({
        title: t('gymAdmin.roundScores.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
      if (errorCodeOf(error) === 'round_closed') onRoundClosed?.();
    } finally {
      setBusyCell(null);
    }
  };

  /**
   * Marks a block as one the athletes cannot score for themselves — or lets
   * them again. The flag is what the member app reads to hide the athlete's
   * own buttons, and what the server checks when one of them tries anyway.
   */
  const setJudgeOnly = async (block: RoundScoreClimb, requiresJudge: boolean) => {
    if (!competitionSlug || !roundId || busyBlock) return;

    setBusyBlock(block.id);
    try {
      const saved = await competitionRoundsService.updateRoundClimb(
        competitionSlug,
        roundId,
        block.id,
        { requires_judge: requiresJudge }
      );
      onClimbChanged({ ...block, requires_judge: saved.requires_judge });
    } catch (error) {
      toast({
        title: t('gymAdmin.roundBlocks.judgeOnlySaveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setBusyBlock(null);
    }
  };

  /**
   * Hands a judge-only block to some of the judges. An empty list opens it to
   * all of them again.
   */
  const assignJudges = async (block: RoundScoreClimb, judgeIds: string[]) => {
    if (!competitionSlug || !roundId) return;

    try {
      const saved = await competitionRoundsService.updateRoundClimb(
        competitionSlug,
        roundId,
        block.id,
        { judge_ids: judgeIds }
      );
      onClimbChanged({ ...block, judge_ids: saved.judge_ids ?? judgeIds });
    } catch (error) {
      toast({
        title: t('gymAdmin.roundBlocks.judges.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
      throw error;
    }
  };

  /**
   * The problems the open class climbs: its own blocks, plus anything the whole
   * round shares — a round run on the gym's own boulders has only the latter.
   * For a judge, only the ones handed to them (or to nobody): a block they may
   * not write on is not on their table.
   */
  const blocks = useMemo(
    () =>
      climbs
        .filter((climb) => climb.category_id === null || climb.category_id === categoryId)
        .filter((climb) => canSetBlocks || climb.can_score !== false)
        .sort((a, b) => a.position - b.position),
    [climbs, categoryId, canSetBlocks]
  );

  /** Everybody in the open class, in the order a start list is read. */
  const entrants = useMemo(
    () =>
      scores
        .filter((score) => score.category_id === categoryId)
        .sort((a, b) =>
          `${a.surname ?? ''} ${a.name ?? ''}`.localeCompare(
            `${b.surname ?? ''} ${b.name ?? ''}`,
            undefined,
            { sensitivity: 'base' }
          )
        ),
    [scores, categoryId]
  );

  /**
   * The table narrowed by what was typed. A block whose name matches is shown
   * whole — everybody on it — and otherwise a block shows the entrants whose
   * name, surname or squad matches. Blocks left with nobody on them drop out.
   */
  const shownGroups = useMemo(
    () =>
      blocks
        .map((block) => ({
          block,
          rows: matchesFilter(filter, block.name)
            ? entrants
            : entrants.filter((score) =>
                matchesFilter(filter, score.name, score.surname, score.full_name, score.team_name)
              ),
        }))
        .filter((group) => group.rows.length > 0),
    [blocks, entrants, filter]
  );

  if (blocks.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        {t('gymAdmin.roundBlocks.noBlocks')}
      </p>
    );
  }

  if (entrants.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        {t('gymAdmin.roundScores.empty')}
      </p>
    );
  }

  if (shownGroups.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        {t('gymAdmin.roundScores.noMatch')}
      </p>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">{t('gymAdmin.roundBlocks.block')}</th>
              <th className="px-3 py-2 text-left font-medium">
                {t('gymAdmin.roundScores.athlete')}
              </th>
              <th className="px-3 py-2 text-left font-medium">
                {t('gymAdmin.roundScores.solved')}
              </th>
              {/* A column of buttons is no use on paper. */}
              <th className="px-3 py-2 text-left font-medium print:hidden">
                {t('gymAdmin.roundBlocks.record')}
              </th>
            </tr>
          </thead>
          {/* A tbody per problem, which is what lets the block's name span its
              whole field: a judge reads down a column rather than hunting for a
              repeated label. */}
          {shownGroups.map(({ block, rows }, groupIndex) => (
            <tbody
              key={block.id}
              data-testid="block-group"
              className={cn(
                // The seam between two problems, against the hairlines between
                // the entrants on one of them: heavier and darker, so a judge
                // running a finger down the table sees where one block's field
                // stops without having to read the names.
                //
                // On the tbody rather than on its last row, because a row's
                // bottom border stops short of the block name — that cell
                // belongs to the *first* row and spans down past the last one.
                'border-b-4 border-b-foreground/40',
                // Nothing under the final group: the card's own edge closes the
                // table.
                groupIndex === shownGroups.length - 1 && 'border-b-0'
              )}
            >
              {rows.map((score, index) => {
                const card = score.cards.find(
                  (candidate) => candidate.round_climb_id === block.id
                );
                const attempts = card?.attempts.map((a) => a.attempt_type) ?? [];

                return (
                  <tr
                    key={`${block.id}-${score.id}`}
                    className="border-b last:border-0"
                    data-testid="block-row"
                  >
                    {index === 0 && (
                      <td
                        rowSpan={rows.length}
                        className="border-r px-3 py-2 align-top font-medium"
                        data-testid="block-name"
                      >
                        {block.name ?? t('gymAdmin.roundScores.unnamedClimb')}
                        {/* Which blocks the athletes cannot score for
                            themselves — the ones a judge has to stand at.
                            Whoever runs the round decides it here; a judge only
                            reads it. */}
                        {canSetBlocks ? (
                          <>
                            <label
                              className="mt-1.5 flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-xs font-normal text-muted-foreground print:hidden"
                              title={t('gymAdmin.roundBlocks.judgeOnlyHint')}
                            >
                              <Checkbox
                                checked={block.requires_judge}
                                disabled={busyBlock === block.id}
                                onCheckedChange={(checked) => setJudgeOnly(block, checked === true)}
                                aria-label={t('gymAdmin.roundBlocks.judgeOnly')}
                                data-testid="block-judge-toggle"
                              />
                              {t('gymAdmin.roundBlocks.judgeOnly')}
                            </label>
                            {/* Who stands at it — only asked once it is a
                                judge's block at all. */}
                            {block.requires_judge && (
                              <div className="print:hidden">
                                <BlockJudgesPicker
                                  judges={judges}
                                  judgeIds={block.judge_ids ?? []}
                                  onChange={(ids) => assignJudges(block, ids)}
                                />
                              </div>
                            )}
                          </>
                        ) : (
                          block.requires_judge && (
                            <span
                              className="ml-1 text-xs font-normal text-muted-foreground"
                              data-testid="block-judge-label"
                            >
                              {t('gymAdmin.roundBlocks.judgeLabel')}
                            </span>
                          )
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2">
                      {[score.name, score.surname].filter(Boolean).join(' ') || score.full_name}
                    </td>
                    <td className="px-3 py-2">
                      {/* Every go in the order it happened, as a judge writes
                          them: `|` a try, `+` the zone, `T` the top. Clicking
                          opens the card whole — which is where one of them comes
                          back off, since the buttons beside it only ever add. */}
                      <button
                        type="button"
                        disabled={!card}
                        onClick={() => card && setOpen({ score, roundClimbId: block.id })}
                        className="inline-flex min-w-16 items-center gap-0.5 rounded px-1 py-0.5 font-mono hover:bg-muted disabled:cursor-default disabled:opacity-50"
                        data-testid="open-block-dialog"
                        title={t('gymAdmin.roundBlocks.editAttempts')}
                      >
                        {attempts.length === 0 && <span className="text-muted-foreground">—</span>}
                        {attempts.map((type, markIndex) => (
                          <span
                            key={`${block.id}-${score.id}-${markIndex}`}
                            className={cn(
                              type === 'zone' && 'text-yellow-600',
                              type === 'top' && 'font-semibold text-green-600',
                              type === 'attempt' && 'text-muted-foreground'
                            )}
                          >
                            {MARK[type]}
                          </span>
                        ))}
                      </button>
                    </td>
                    <td className="px-3 py-2 print:hidden">
                      {/* One press, one go — this is the surface somebody uses
                          standing at the wall. A top ends the card, so once it
                          is on there is nothing more to add; so does closing
                          the round, which ends them all. */}
                      {canScore && card && !attempts.includes('top') && (
                        <div
                          className="flex flex-wrap items-center gap-1"
                          data-testid="record-attempt"
                        >
                          {busyCell === cellKey(block.id, score.id) && (
                            <Loader2 className="size-4 animate-spin text-muted-foreground" />
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            disabled={!!busyCell}
                            onClick={() => addMark(score, block.id, 'attempt')}
                            data-testid="record-attempt-try"
                          >
                            {t('judgeBoulder.attempt')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 border-yellow-600 text-xs text-yellow-600 hover:bg-yellow-50"
                            disabled={!!busyCell}
                            onClick={() => addMark(score, block.id, 'zone')}
                            data-testid="record-attempt-zone"
                          >
                            <Plus className="mr-1 size-3" />
                            {t('judgeBoulder.zone')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 border-green-600 text-xs text-green-600 hover:bg-green-50"
                            disabled={!!busyCell}
                            onClick={() => addMark(score, block.id, 'top')}
                            data-testid="record-attempt-top"
                          >
                            <Trophy className="mr-1 size-3" />
                            {t('judgeBoulder.top')}
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          ))}
        </table>
      </div>

      <RoundScoreDialog
        open={open !== null}
        onOpenChange={(next) => !next && setOpen(null)}
        competitionSlug={competitionSlug}
        roundId={roundId}
        score={open?.score ?? null}
        roundClimbId={open?.roundClimbId}
        canScore={canScore}
        judgedBlocks={judgedBlocks}
        onSaved={(saved) => {
          onScoreSaved(saved);
          setOpen(null);
        }}
        onRoundClosed={onRoundClosed}
      />
    </>
  );
}
